import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import { RouteConstants } from '../../../navigation/navigation-types'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { SurveyResponseBriefDto } from '../../../services/api/sharedTypes/Email'
import { convertSurveyTypeToString } from '../../../utils/email-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Událost',
    },
    {
        id: 'respondentName',
        numeric: false,
        disablePadding: false,
        label: 'Brigádník/Klient',
    },
    {
        id: 'eventStartsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
    },
    {
        id: 'eventEndsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
    },
    {
        id: 'address',
        numeric: false,
        disablePadding: false,
        label: 'Místo',
    },
    {
        id: 'surveyType',
        numeric: false,
        disablePadding: false,
        label: 'Dotazník',
    },
    {
        id: 'responseCreatedAt',
        numeric: false,
        disablePadding: false,
        label: 'Vytvořen',
    },
]

interface NewlyFilledSurveysTableProps {
    newlyFilledSurveys: SurveyResponseBriefDto[]
}

const NewlyFilledSurveysTable: React.FC<NewlyFilledSurveysTableProps> = ({
    newlyFilledSurveys,
}: NewlyFilledSurveysTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('responseCreatedAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {newlyFilledSurveys.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={newlyFilledSurveys.length}
                        />
                        <TableBody>
                            {stableSort(
                                newlyFilledSurveys.map((row) => {
                                    return {
                                        ...row,
                                        eventIdName: row.eventIdName.name,
                                        eventName: row.eventIdName.name,
                                        eventId: row.eventIdName.id ?? '',
                                        respondentIdName: row.respondentIdName.name,
                                        respondentName: row.respondentIdName.name,
                                        respondentId: row.respondentIdName.id ?? '',
                                        address: row.address.city + ', ' + row.address.street + ' ' + row.address.zip,
                                        eventEndsAt: new Date(row.eventEndsAt).getTime(),
                                        eventStartsAt: new Date(row.eventStartsAt).getTime(),
                                        responseCreatedAt: new Date(row.responseCreatedAt).getTime(),
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow hover tabIndex={-1} key={index} style={{ cursor: 'pointer' }}>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {row.eventName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {row.respondentName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {new Date(row.eventStartsAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {new Date(row.eventEndsAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {row.address}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {convertSurveyTypeToString(row.surveyType)}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                route={
                                                    RouteConstants.emailsAndSurveys + '/' + row.id + '#' + Number(row.surveyType)
                                                }
                                                isText
                                            >
                                                {new Date(row.responseCreatedAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={newlyFilledSurveys.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default NewlyFilledSurveysTable
